/**
 * Tour configurations for product walkthroughs
 * Using @reactour/tour (React 19 compatible)
 */

import React from 'react';
import { StepType } from '@reactour/tour';

export type TourId = 
  | 'first-time-user' 
  | 'onboarding-scenario-detail'
  | 'onboarding-scenario-comparison'
  | 'onboarding-shock-parameters'
  | 'onboarding-borrowing-instruments'
  | 'onboarding-stochastic-projection';

export interface TourConfig {
  id: TourId;
  title: string;
  description: string;
  steps: StepType[];
}

/**
 * First-Time User Tour
 * Introduces new users to the platform
 */
export const firstTimeUserTour: TourConfig = {
  id: 'first-time-user',
  title: 'Welcome to DT DebtCast',
  description: 'Get started with a quick tour of the platform',
  steps: [
    {
      selector: 'body',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Welcome to DT DebtCast!</h3>
          <p>This tour will help you get started with debt sustainability analysis.</p>
        </div>
      ),
      position: 'center',
    },
    {
      selector: '[data-tour="sidebar"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Navigation</h3>
          <p>Use the sidebar to navigate between different sections of the application.</p>
        </div>
      ),
      position: 'right',
    },
    {
      selector: '[data-tour="sidebar-trigger"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Collapse Sidebar</h3>
          <p>Click this button to collapse or expand the sidebar. This gives you more screen space when you need it.</p>
        </div>
      ),
      position: 'bottom',
    },
    {
      selector: '[data-tour="scenario-analysis"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Scenario Analysis</h3>
          <p>Analyze debt sustainability under different economic scenarios. Compare baseline projections with various shocks.</p>
        </div>
      ),
      position: 'right',
    },
    {
      selector: '[data-tour="stochastic-projection"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Stochastic Projections</h3>
          <p>Generate Monte Carlo simulations to understand uncertainty and risk in debt projections.</p>
        </div>
      ),
      position: 'right',
    },
    {
      selector: '[data-tour="user-menu"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">User Menu</h3>
          <p>Click your profile picture to access account settings, reset your progress, and access debug tools (in development).</p>
        </div>
      ),
      position: 'bottom',
    },
    {
      selector: '[data-tour="help-button"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Need Help?</h3>
          <p>Click the help icon anytime to access documentation, guides, and tutorials.</p>
        </div>
      ),
      position: 'bottom',
    },
  ],
};

/**
 * Onboarding: Scenario Detail Tour
 * Guides users through selecting scenarios and understanding the output
 */
export const onboardingScenarioDetailTour: TourConfig = {
  id: 'onboarding-scenario-detail',
  title: 'Scenario Analysis Tutorial',
  description: 'Learn how to select scenarios and understand the output',
  steps: [
    {
      selector: '[data-tour="scenario-overview"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Scenario Overview</h3>
          <p className="mb-2">This section shows:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>State Information:</strong> Current credit rating and agency</li>
            <li><strong>Scenario Description:</strong> What assumptions this scenario uses</li>
            <li><strong>Projection Period:</strong> Years covered by the analysis</li>
          </ul>
          <p className="mt-2 text-sm">This helps you understand the context and assumptions behind each scenario.</p>
        </div>
      ),
      position: 'top',
    },
    {
      selector: '[data-tour="scenario-tables"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Key Indicators Table</h3>
          <p className="mb-2">This table shows detailed year-by-year projections for:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Debt-to-GDP:</strong> Total debt as % of state GDP (lower is better)</li>
            <li><strong>Gross Financing Needs:</strong> New borrowing required each year</li>
            <li><strong>GDP Growth:</strong> Economic growth projections</li>
            <li><strong>Interest Rates:</strong> Borrowing costs over time</li>
          </ul>
          <p className="mt-2 text-sm">Use the indicator filter dropdown above to focus on specific metrics. The data shows historical averages (2015-2018) and year-by-year projections.</p>
        </div>
      ),
      position: 'top',
    },
    {
      selector: '[data-tour="scenario-selector"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Select a Scenario</h3>
          <p className="mb-2">Use this dropdown to choose different economic scenarios:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Baseline:</strong> Standard projections based on current trends</li>
            <li><strong>Revenue Shock:</strong> Simulates a drop in state revenue</li>
            <li><strong>Expenditure Shock:</strong> Simulates an increase in state spending</li>
            <li><strong>Interest Rate Shock:</strong> Simulates higher borrowing costs</li>
            <li><strong>Intergovernmental Revenue Shock:</strong> Simulates changes in federal funding</li>
          </ul>
          <p className="mt-2 text-sm">Try selecting different scenarios to see how they affect debt sustainability.</p>
        </div>
      ),
      position: 'bottom',
    },
  ],
};

/**
 * Onboarding: Scenario Comparison Tour
 * Guides users through comparing multiple scenarios
 */
export const onboardingScenarioComparisonTour: TourConfig = {
  id: 'onboarding-scenario-comparison',
  title: 'Scenario Comparison Tutorial',
  description: 'Learn how to compare multiple scenarios side-by-side',
  steps: [
    {
      selector: '[data-tour="scenario-comparison-tab"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Switch to Comparison View</h3>
          <p className="mb-2">Click the &quot;Scenario Comparison&quot; tab to see multiple scenarios side-by-side.</p>
          <p className="text-sm">This view allows you to compare how different economic conditions affect debt sustainability metrics simultaneously.</p>
        </div>
      ),
      position: 'bottom',
    },
    {
      selector: '[data-tour="comparison-charts"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Comparison Charts</h3>
          <p className="mb-2">Each chart shows multiple scenario lines:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Debt-to-GDP:</strong> Compare debt levels across scenarios</li>
            <li><strong>Gross Financing Needs:</strong> See funding requirements</li>
            <li><strong>Debt Service:</strong> Compare interest and principal payments</li>
            <li><strong>Debt-to-Revenue:</strong> Compare debt relative to revenues</li>
          </ul>
          <p className="mt-2 text-sm">Look for scenarios where metrics remain sustainable (stable or declining ratios). Higher lines indicate greater risk.</p>
        </div>
      ),
      position: 'top',
    },
  ],
};

/**
 * Onboarding: Shock Parameters Tour
 * Guides users through configuring shock parameters
 */
export const onboardingShockParametersTour: TourConfig = {
  id: 'onboarding-shock-parameters',
  title: 'Shock Parameters Tutorial',
  description: 'Learn how to configure shock parameters and their effects',
  steps: [
    {
      selector: '[data-tour="shock-parameters-button"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Open Shock Parameters</h3>
          <p className="mb-2">Click the &quot;Shock Parameters&quot; button to configure how economic shocks affect your scenarios.</p>
          <p className="text-sm">Shock parameters control the magnitude, duration, and type of economic disruptions used in shock scenarios.</p>
          <p className="mt-2 text-sm font-medium">Click the button to open the modal and continue the tour.</p>
        </div>
      ),
      position: 'bottom',
    },
    {
      selector: '[data-tour="shock-modal-revenue"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Revenue Shock Configuration</h3>
          <p className="mb-2">Configure revenue shocks:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Type:</strong> Choose &quot;Stdev&quot; for statistical shock based on historical standard deviation, or &quot;Manual&quot; for a fixed percentage change</li>
            <li><strong>Duration:</strong> Set how many years the shock lasts (1-10 years). Longer duration increases cumulative impact</li>
            <li><strong>Magnitude:</strong> If Type is &quot;Stdev&quot;, enter standard deviation multiplier (e.g., 1.0 = 1 standard deviation). If &quot;Manual&quot;, enter percentage change (e.g., -5.0 = 5% revenue decrease)</li>
          </ul>
          <p className="mt-2 text-sm"><strong>Effect:</strong> Revenue shocks simulate drops in state revenue. Higher magnitude and longer duration increase the impact on debt sustainability.</p>
        </div>
      ),
      position: 'top',
    },
    {
      selector: '[data-tour="shock-modal-intergovernmental"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Intergovernmental Revenue Shock Configuration</h3>
          <p className="mb-2">Configure intergovernmental revenue shocks:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Type:</strong> Choose &quot;Stdev&quot; for statistical shock based on historical standard deviation, or &quot;Manual&quot; for a fixed percentage change</li>
            <li><strong>Duration:</strong> Set how many years the shock lasts (1-10 years). Longer duration increases cumulative impact</li>
            <li><strong>Magnitude:</strong> If Type is &quot;Stdev&quot;, enter standard deviation multiplier (e.g., 1.0 = 1 standard deviation). If &quot;Manual&quot;, enter percentage change (e.g., -5.0 = 5% intergovernmental revenue decrease)</li>
          </ul>
          <p className="mt-2 text-sm"><strong>Effect:</strong> Intergovernmental revenue shocks simulate changes in federal funding and other intergovernmental transfers. Decreases in these revenues can significantly impact state budgets and debt sustainability.</p>
        </div>
      ),
      position: 'top',
    },
    {
      selector: '[data-tour="shock-modal-expenditure"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Expenditure Shock Configuration</h3>
          <p className="mb-2">Configure expenditure shocks:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Type:</strong> Choose &quot;Stdev&quot; for statistical shock or &quot;Manual&quot; for a fixed percentage change</li>
            <li><strong>Duration:</strong> Set how many years the shock persists (1-10 years)</li>
            <li><strong>Magnitude:</strong> If Type is &quot;Stdev&quot;, enter standard deviation multiplier. If &quot;Manual&quot;, enter percentage change (e.g., 5.0 = 5% spending increase)</li>
          </ul>
          <p className="mt-2 text-sm"><strong>Effect:</strong> Expenditure shocks simulate increases in state spending. Higher spending increases financing needs and debt levels, showing how policy changes affect debt sustainability.</p>
        </div>
      ),
      position: 'right',
    },
    {
      selector: '[data-tour="shock-modal-interest"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Interest Rate Shock Configuration</h3>
          <p className="mb-2">Configure interest rate shocks:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Basis Points:</strong> Enter the increase in borrowing costs (100 basis points = 1 percentage point)</li>
          </ul>
          <p className="mt-2 text-sm"><strong>Effect:</strong> Interest rate shocks simulate higher borrowing costs. Higher rates increase debt service costs and financing needs, helping assess vulnerability to rate changes.</p>
        </div>
      ),
      position: 'right',
    },
    {
      selector: '[data-tour="shock-modal-apply"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Apply Changes</h3>
          <p className="mb-2">Click &quot;Apply Shock Parameters&quot; to save your changes.</p>
          <p className="text-sm"><strong>What happens:</strong> All shock scenarios (Revenue, Intergovernmental Revenue, Expenditure, Interest Rate) will recalculate using your new parameters. The comparison charts will update to show the new projections.</p>
          <p className="mt-2 text-sm font-medium">After applying, you&apos;ll be taken back to the getting started checklist to continue your journey!</p>
        </div>
      ),
      position: 'top',
    },
  ],
};

/**
 * Onboarding: Borrowing Instruments Tour
 * Guides users through configuring borrowing instruments
 */
export const onboardingBorrowingInstrumentsTour: TourConfig = {
  id: 'onboarding-borrowing-instruments',
  title: 'Borrowing Instruments Tutorial',
  description: 'Learn how to configure debt instruments and their effects',
  steps: [
    {
      selector: '[data-tour="new-borrowing-button"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Open Borrowing Instruments</h3>
          <p className="mb-2">Click the &quot;New Borrowing&quot; button to configure your debt instruments.</p>
          <p className="text-sm">Borrowing instruments define the types of debt (bonds, loans) used in calculations, including their interest rates, maturity, and allocation.</p>
          <p className="mt-2 text-sm font-medium">Click the button to open the modal and continue the tour.</p>
        </div>
      ),
      position: 'bottom',
    },
    {
      selector: '[data-tour="borrowing-modal-table"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Borrowing Instruments Table</h3>
          <p className="mb-2">This table shows your current debt instruments. Each instrument has:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Instrument:</strong> Name (e.g., &quot;5-Year Bond&quot;)</li>
            <li><strong>Interest Rate:</strong> Annual borrowing cost (%)</li>
            <li><strong>Maturity:</strong> Years until repayment</li>
            <li><strong>Type:</strong> Short-Term (≤2 years) or Long-Term (&gt;2 years)</li>
            <li><strong>Share:</strong> Percentage allocation (must sum to 100%)</li>
          </ul>
          <p className="mt-2 text-sm">You can modify existing instruments or add new ones using the &quot;Add Instrument&quot; button.</p>
        </div>
      ),
      position: 'right',
    },
    {
      selector: '[data-tour="borrowing-modal-add"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Add New Instrument</h3>
          <p className="mb-2">Click &quot;Add Instrument&quot; to create a new debt instrument.</p>
          <p className="text-sm">When adding, you&apos;ll specify:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>Interest rate (affects borrowing costs)</li>
            <li>Maturity (affects repayment schedule)</li>
            <li>Share (allocation percentage)</li>
          </ul>
          <p className="mt-2 text-sm"><strong>Effect:</strong> Different instruments affect interest calculations and debt service projections. Longer maturities spread payments over time, while higher rates increase costs.</p>
        </div>
      ),
      position: 'top',
    },
    {
      selector: '[data-tour="borrowing-modal-apply"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Apply Changes</h3>
          <p className="mb-2">Click &quot;Close the Gap&quot; to save your borrowing instrument changes.</p>
          <p className="text-sm"><strong>What happens:</strong> All scenarios will recalculate using your new instrument configuration. Interest costs, debt service, and financing needs will update based on your instrument mix.</p>
          <p className="mt-2 text-sm font-medium">After applying, you&apos;ll be taken back to the getting started checklist to continue your journey!</p>
        </div>
      ),
      position: 'top',
    },
  ],
};

/**
 * Onboarding: Stochastic Projection Tour
 * Comprehensive walkthrough of stochastic projections and global settings
 */
export const onboardingStochasticProjectionTour: TourConfig = {
  id: 'onboarding-stochastic-projection',
  title: 'Stochastic Projection Tutorial',
  description: 'Learn how to configure stochastic projections and understand global settings effects',
  steps: [
    {
      selector: '[data-tour="projection-parameters"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Projection Parameters</h3>
          <p className="mb-2">Configure your stochastic projection:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Target Debt-to-GDP:</strong> Policy goal - affects the median baseline projection</li>
            <li><strong>Number of Simulations:</strong> More simulations (10,000+) = more accurate but slower</li>
            <li><strong>Projection Years:</strong> How far into the future to project (max 10 years)</li>
            <li><strong>Use Historical Covariance:</strong> Enable for realistic correlations between variables</li>
          </ul>
          <p className="mt-2 text-sm">These parameters control the Monte Carlo simulation that generates the fan chart.</p>
        </div>
      ),
      position: 'bottom',
    },
    {
      selector: '[data-tour="global-settings-impact"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Global Settings Impact</h3>
          <p className="mb-2">Stochastic projections are affected by global settings:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Shock Parameters:</strong> Affect the variance and correlation in simulations</li>
            <li><strong>Borrowing Instruments:</strong> Determine interest rate assumptions used in projections</li>
            <li><strong>Historical Years:</strong> Data range used to calculate statistical properties</li>
          </ul>
          <p className="mt-2 text-sm">Changes to global settings will affect the uncertainty bands and median projection in the fan chart.</p>
        </div>
      ),
      position: 'bottom',
    },
    {
      selector: '[data-tour="fan-chart"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Fan Chart</h3>
          <p className="mb-2">The fan chart visualizes uncertainty:</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Historical Line:</strong> Actual past debt-to-GDP ratios</li>
            <li><strong>Baseline Projection:</strong> Expected future path (dashed line)</li>
            <li><strong>Shaded Bands:</strong> Probability ranges - wider = more uncertainty</li>
            <li><strong>Percentile Bands:</strong> Different shades show different probability levels</li>
          </ul>
          <p className="mt-2 text-sm">Wider bands indicate higher uncertainty. If the baseline stays within lower percentiles, debt is likely sustainable.</p>
        </div>
      ),
      position: 'top',
    },
    {
      selector: '[data-tour="generate-projection"]',
      content: (
        <div>
          <h3 className="font-semibold mb-2">Generate Projection</h3>
          <p className="mb-2">Click &quot;Regenerate Projection&quot; to run the Monte Carlo simulation.</p>
          <p className="text-sm"><strong>What happens:</strong> The system runs thousands of simulations using your parameters and global settings, then displays the results as a fan chart showing the range of possible outcomes.</p>
          <p className="mt-2 text-sm font-medium">After generating, you can adjust parameters and regenerate to see how assumptions affect the uncertainty range!</p>
        </div>
      ),
      position: 'top',
    },
  ],
};

/**
 * Get tour configuration by ID
 */
export function getTourConfig(tourId: TourId): TourConfig | undefined {
  const tours: Record<TourId, TourConfig> = {
    'first-time-user': firstTimeUserTour,
    'onboarding-scenario-detail': onboardingScenarioDetailTour,
    'onboarding-scenario-comparison': onboardingScenarioComparisonTour,
    'onboarding-shock-parameters': onboardingShockParametersTour,
    'onboarding-borrowing-instruments': onboardingBorrowingInstrumentsTour,
    'onboarding-stochastic-projection': onboardingStochasticProjectionTour,
  };
  return tours[tourId];
}

/**
 * Get all tour configurations
 */
export function getAllTours(): TourConfig[] {
  return [
    firstTimeUserTour,
    onboardingScenarioDetailTour,
    onboardingScenarioComparisonTour,
    onboardingShockParametersTour,
    onboardingBorrowingInstrumentsTour,
    onboardingStochasticProjectionTour,
  ];
}
